import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import Button from '../components/Button';
import { colors } from '../theme/tokens';
import logoHorizontal from '../assets/logo-horizontal.png';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';

const ResetPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.neutral.offWhite};
`;

const ResetPasswordForm = styled.div`
  background: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 2rem;
`;

const TitleSection = styled.div`
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.neutral.darkCharcoal};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-size: 1rem;
  background-color: ${colors.neutral.white};
  color: ${colors.neutral.black};
  
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
    background-color: ${colors.neutral.offWhite};
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  color: ${colors.brand.laurel};
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const BackToLogin = styled.div`
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${colors.neutral.darkCharcoal};
  
  a {
    color: ${colors.brand.laurel};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if we have a valid session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      }
    };
    checkSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'An error occurred while resetting your password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResetPasswordContainer>
      <ResetPasswordForm>
        <Logo src={logoHorizontal} alt="Appetite Admin" />
        <TitleSection>
          <TitleLarge>Reset Password</TitleLarge>
          <SubtitleMedium>Enter your new password</SubtitleMedium>
        </TitleSection>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={success}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={success}
            />
          </FormGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && (
            <SuccessMessage>
              Password reset successful! Redirecting to login...
            </SuccessMessage>
          )}
          <Button type="submit" disabled={isLoading || success}>
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
          <BackToLogin>
            <a href="/login">Back to Login</a>
          </BackToLogin>
        </form>
      </ResetPasswordForm>
    </ResetPasswordContainer>
  );
};

export default ResetPassword; 